// The Tasks list's filters, pure (no React) so taskModel.selftest.ts runs
// them on their own: what the URL says (the search, Anyone, the status, the
// whole history, and what only a link sets), the status filter's choices,
// what the server is asked (the one-off work and a schedule's runs, apart)
// and the narrowing done here. Split from taskModel.ts (the status words
// and rules it uses).

import type { TaskKind, TaskQuery, TaskStatus, TaskSummary } from "@/api/tasks";
import { routineDisplayName } from "@/lib/routineNames";

import { ENDED_STATUSES, LIVE_STATUSES, askerName, isTaskStatus, taskGroup, taskStatusWord, workerName, type TaskGroupKey } from "./taskModel";

// ── filters, kept in the URL ──
//
// The bar: one search box (`q`, free text over the title, the conclusion
// and who), "Anyone" (`who`: an agent or subagent, what it did or asked
// for) and "Any status" (statusChoice: `?status=needs_you|live` or exact
// statuses `?status=finished`, `failed,timed_out,partial`, `stopped`).
// Links from elsewhere keep working: `?status=ended|working`, any other set
// of TaskStatus names, `worker` and `asker` (an agent's template,
// "operator" = you, "schedule"), `kind` task|question; what the bar does
// not show is a removable chip (listModel.filterChips). `?show=all` is the
// whole history (TaskList: older tasks by month, more of each read).

export type TaskView = "all" | TaskGroupKey;

export interface TaskFilters {
  q: string;
  view: TaskView;
  /** Exact statuses; empty = the view decides. */
  status: TaskStatus[];
  /** An agent or subagent: the tasks it is doing or gave (`?who=`). */
  who: string;
  worker: string;
  asker: string;
  kind: "" | TaskKind;
  /** The whole history (`?show=all`). */
  everything: boolean;
}

export const NO_TASK_FILTERS: TaskFilters = { q: "", view: "all", status: [], who: "", worker: "", asker: "", kind: "", everything: false };

/** The query parameters the filters use. */
export const TASK_FILTER_PARAMS = ["q", "status", "who", "worker", "asker", "kind", "show"] as const;

const VIEWS: readonly string[] = ["needs_you", "live", "ended"];

/**
 * `?status=working` (the bell's link) means what Running shows, every live
 * task (queued, waiting for an answer, paused too), not that one status.
 */
const VIEW_ALIASES: Readonly<Record<string, TaskView>> = { working: "live" };

export function filtersFromParams(params: URLSearchParams): TaskFilters {
  const raw = (params.get("status") ?? "").trim();
  const view: TaskView = VIEWS.includes(raw) ? (raw as TaskView) : (VIEW_ALIASES[raw] ?? "all");
  const status = view === "all" ? raw.split(",").map((s) => s.trim()).filter(isTaskStatus) : [];
  const kind = params.get("kind");
  return {
    q: params.get("q") ?? "",
    view,
    status: [...new Set(status)],
    who: params.get("who")?.trim() ?? "",
    worker: params.get("worker")?.trim() ?? "",
    asker: params.get("asker")?.trim() ?? "",
    kind: kind === "task" || kind === "question" ? kind : "",
    everything: params.get("show") === "all",
  };
}

/** `params` with the filters written in (an "any" filter leaves no parameter). */
export function withFilterParams(params: URLSearchParams, f: TaskFilters): URLSearchParams {
  const next = new URLSearchParams(params);
  const status = f.view !== "all" ? f.view : f.status.join(",");
  const values: Record<(typeof TASK_FILTER_PARAMS)[number], string> = {
    q: f.q,
    status,
    who: f.who,
    worker: f.worker,
    asker: f.asker,
    kind: f.kind,
    show: f.everything ? "all" : "",
  };
  for (const key of TASK_FILTER_PARAMS) {
    if (values[key]) next.set(key, values[key]);
    else next.delete(key);
  }
  return next;
}

// ── the status filter ──

/** What "Any status" offers; "other" is a view or a set of statuses only a link makes. */
export type StatusChoice = "" | "needs_you" | "live" | "finished" | "trouble" | "stopped";

const TROUBLE: readonly TaskStatus[] = ["failed", "timed_out", "partial"];

const CHOICE_STATUSES: Readonly<Record<"finished" | "trouble" | "stopped", readonly TaskStatus[]>> = {
  finished: ["finished"],
  trouble: TROUBLE,
  stopped: ["stopped"],
};

export const STATUS_CHOICES: readonly { value: StatusChoice; label: string }[] = [
  { value: "", label: "Any status" },
  { value: "needs_you", label: "Needs you" },
  { value: "live", label: "Running" },
  { value: "finished", label: "Done" },
  { value: "trouble", label: "Failed or unfinished" },
  { value: "stopped", label: "Stopped" },
];

const sameSet = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x) => b.includes(x));

/** The status filter as the bar shows it; "other" for what only a link makes (`?status=ended`, `?status=failed`). */
export function statusChoice(f: Pick<TaskFilters, "view" | "status">): StatusChoice | "other" {
  if (f.view === "needs_you" || f.view === "live") return f.view;
  if (f.view === "ended") return "other";
  if (!f.status.length) return "";
  for (const [choice, statuses] of Object.entries(CHOICE_STATUSES)) if (sameSet(f.status, statuses)) return choice as StatusChoice;
  return "other";
}

/** The filters with a status choice made (exact statuses or a view, never both). */
export function withStatusChoice(f: TaskFilters, choice: StatusChoice): TaskFilters {
  if (choice === "") return { ...f, view: "all", status: [] };
  if (choice === "needs_you" || choice === "live") return { ...f, view: choice, status: [] };
  return { ...f, view: "all", status: [...CHOICE_STATUSES[choice]] };
}

/** What an "other" status filter is called: "Ended", "Failed, Out of time". */
export function otherStatusLabel(f: Pick<TaskFilters, "view" | "status">): string {
  if (f.view === "ended") return "Ended";
  return f.status.map((s) => taskStatusWord(s).label).join(", ");
}

// ── what the server is asked ──

/** How many tasks a read asks for (the route allows 1..500): the usual list, and the whole history. */
export const LIST_LIMIT = 200;
export const EVERYTHING_LIMIT = 500;

/** Statuses a status filter stands for ([] = any). "Needs you" asks for everything: an answer waiting can be on an ended task. */
function statusesOf(f: TaskFilters): TaskStatus[] {
  return f.status.length ? [...f.status] : f.view === "live" ? [...LIVE_STATUSES] : f.view === "ended" ? [...ENDED_STATUSES] : [];
}

/**
 * The one-off work: every task but a schedule's runs that ended with
 * nothing waiting (those are read apart, scheduledQuery, so a busy schedule
 * never pushes one-off work out of the read). Who, the kind and the
 * statuses are asked for; free text is narrowed here.
 */
export function serverQuery(f: TaskFilters): TaskQuery {
  const status = statusesOf(f);
  return {
    worker: f.worker || undefined,
    asker: f.asker || undefined,
    agent: f.who || undefined,
    kind: f.kind || undefined,
    status: status.length ? status : undefined,
    limit: f.everything ? EVERYTHING_LIMIT : LIST_LIMIT,
    includeEndedScheduled: false,
  };
}

/**
 * A schedule's runs, for the Scheduled group (folded per schedule), or
 * null when the filters leave them out: nothing needs you or runs there
 * (those are in the one-off read), or another asker was asked for.
 */
export function scheduledQuery(f: TaskFilters): TaskQuery | null {
  if (f.view === "needs_you" || f.view === "live") return null;
  if (f.asker && f.asker !== "schedule") return null;
  const status = statusesOf(f).filter((s) => !LIVE_STATUSES.includes(s) && s !== "awaiting_approval");
  if (statusesOf(f).length && !status.length) return null;
  return {
    worker: f.worker || undefined,
    asker: "schedule",
    agent: f.who || undefined,
    kind: f.kind || undefined,
    status: status.length ? status : undefined,
    limit: f.everything ? EVERYTHING_LIMIT : LIST_LIMIT,
  };
}

/** True when anything narrows the list (the empty state then offers "Clear filters"). */
export function isFiltered(f: TaskFilters): boolean {
  return !!(f.q.trim() || f.view !== "all" || f.status.length || f.who || f.worker || f.asker || f.kind);
}

/** The narrowing done here, on what the server sent (its own filters are applied again, harmlessly). */
export function taskMatches(t: TaskSummary, f: TaskFilters, nameOf: (template: string) => string = (s) => s): boolean {
  // A view shows exactly its group: an ended task whose answer waits is under Needs you.
  if (f.view !== "all" && taskGroup(t) !== f.view) return false;
  if (f.status.length && !(f.status as string[]).includes(t.status)) return false;
  if (f.kind && t.kind !== f.kind) return false;
  if (f.who && t.asker.agent !== f.who && t.worker.template !== f.who) return false;
  const q = f.q.trim().toLowerCase();
  if (!q) return true;
  // A scheduled run is found by its schedule's name in words too ("check-in" for lattice-checkin).
  const shownAs = t.asker.kind === "schedule" ? routineDisplayName(t.title) : "";
  const hay = [t.title, shownAs, t.conclusion ?? "", workerName(t.worker, nameOf), askerName(t.asker, nameOf)].join(" ").toLowerCase();
  return q.split(/\s+/).every((part) => hay.includes(part));
}
